import { types, getParent } from 'mobx-state-tree'
import BomEntry from './CalculatedMaterialRequirement'
// import LabourEntry from './CalculatedLabourOperation'
import AdditionalCostEntry from './CalculatedAddtionalCost'

export default types.model({
  qty: 1,
  multiplier: 1.2,
  dollarsPerHour: 75,
  bom: types.optional(types.array(types.late(() => BomEntry)), []),
  // labour: types.optional(types.array(types.late(() => LabourEntry)), []),
  additionalCosts: types.optional(types.array(types.late(() => AdditionalCostEntry)), [])
}).views(self => ({
  get isValid() {
    return ((self.qty > 0) && (!isNaN(self.multiplier)) && (!isNaN(self.dollarsPerHour)))
  },
  sumMaterials() {
    return self.bom.reduce((sum, current) => sum += current.extendedCost(), 0)
  },
  sumLabourMinutes() {
    return self.labour.reduce((sum, i) => sum += i.totalMinutes, 0)
  },
  sumLabourDollars() {
    return self.sumLabourMinutes() / 60 * self.dollarsPerHour
  },
  sumAdditionalCosts() {
    return self.additionalCosts.reduce((sum, i) => sum += i.cost, 0)
  },
  calculateMarginCost() {
    return self.sumAll() * (self.multiplier - 1)
  },
  sumAll() {
    return self.sumMaterials() + self.sumLabourDollars() + self.sumAdditionalCosts()
  },
  sumAllWithMargin() {
    return self.sumAll() + self.calculateMarginCost()
  },
  costPerUnit() {
    return self.sumAllWithMargin() / self.qty
  },
  get multiplierAsPercentage() {
    return ((self.multiplier - 1) * 100).toFixed(0)
  },
  get labour() {
    const job = getParent(self, 2)
    return job.labour.map(op => {
      const setupMinutes = op.minutesPerSetup * op.numberOfSetups
      const operationMinutes = Math.ceil((op.secondsPerOperation * op.numberOfOperations * self.qty) / 60)
      const extraMinutes = op.extraMinutesPerUnit * self.qty
      const totalMinutes = setupMinutes + operationMinutes + extraMinutes
      const totalDollars = (totalMinutes / 60) * self.dollarsPerHour
      return {
        description: op.description,
        setupMinutes: setupMinutes,
        operationMinutes: operationMinutes,
        extraMinutes: extraMinutes,
        totalMinutes: setupMinutes + operationMinutes + extraMinutes,
        totalDollars: totalDollars
      }
    })
  }
})).actions(self => ({
  setQty(qty) {
    self.qty = qty
  },
  setMultiplier(float) {
    self.multiplier = float
  },
  setDollarsPerHour(float) {
    self.dollarsPerHour = float
  },
  unsetBom() {
    self.bom = []
  },
  unsetLabour() {
    self.labour = []
  },
  unsetAdditionalCosts() {
    self.additionalCosts = []
  },
  addBomEntry(snapshot) {
    self.bom.push(BomEntry.create(snapshot))
  },
  // addLabourEntry(snapshot) {
  //   self.labour.push(LabourEntry.create(snapshot))
  // },
  addAdditionalCostsEntry(snapshot) {
    self.additionalCosts.push(AdditionalCostEntry.create(snapshot))
  },
  recalculateBom() {
    const job = getParent(self, 2)
    const parts = job.parts
    let bom = {}
    parts.forEach((part, index) => {
      // const key = part.materialDescription ? part.materialDescription : part.name
      var key
      if (part.materialId) {
        key = part.materialId
      } else if (part.materialDescription) {
        key = part.materialDescription
      } else {
        key = part.name + '' + (index + 1) 
      }
      if (!bom[key]) bom[key] = { parts: [], units: part.materialUnits, unitCost: part.materialUnitCost }
      bom[key].description = part.materialDescription ? part.materialDescription : key
      if (part.materialId) bom[key].materialId = part.materialId
      if (part.materialStockLength) bom[key].stockLength = part.materialStockLength
      if (part.materialSheetSizeX) bom[key].sheetSizeX = part.materialSheetSizeX
      if (part.materialSheetSizeY) bom[key].sheetSizeY = part.materialSheetSizeY
      bom[key].parts.push(part)
    })
    Object.keys(bom).forEach((key, index) => {
      const parts = bom[key].parts
      switch (bom[key].units) {
        case 'sheet':
          const sheetSizeX = bom[key].sheetSizeX
          const sheetSizeY = bom[key].sheetSizeY
          var sheetsRequired = []
          parts.forEach(part => {
            const dimX = part.cutLength >= part.cutWidth ? part.cutLength / 1000 : part.cutWidth / 1000
            const dimY = part.cutLength >= part.cutWidth ? part.cutWidth / 1000 : part.cutLength / 1000
            const qtyRequired = part.qty * self.qty
            // Check we are not trying to cut longer than stock lengths
            if (dimX > sheetSizeX) {
              console.error('dimX is greater than sheetSizeX')
              bom[key].totalUnitsRequired = '[ERROR: dimX > sheetSizeX]'
            } else {
              // Find number possible per sheet
              // const numberInXDirection = Math.floor(sheetSizeX / dimX)
              const numberInYDirection = Math.floor(sheetSizeY / dimY)
              // const numberPerSheet = numberInXDirection * numberInYDirection
              
              let cutSoFar = 0;
              let i = 0;
              while (cutSoFar < qtyRequired) {
                if (!(sheetsRequired.length > i)) {
                  sheetsRequired.push({
                    availableX: sheetSizeX,
                    availableY: sheetSizeY
                  })
                }
                if (dimX <= sheetsRequired[i].availableX) {
                  // $num_cross = floor($unit_width / $width);
                  sheetsRequired[i].availableX -= dimX;
                  cutSoFar += numberInYDirection;
                  i = 0;
                } else {
                  i++;
                }
              }
            }
          })
          bom[key].totalUnitsRequired = sheetsRequired.length
          bom[key].totalCost = bom[key].totalUnitsRequired * bom[key].unitCost
          break
        case 'metre':
          if (bom[key].stockLength) {
            const stockLength = bom[key].stockLength
            var lengthsRequired = [];
            parts.forEach(part => {
              const cutLength = part.cutLength / 1000
              const qtyRequired = part.qty * self.qty
              if (cutLength > stockLength) {
                console.error('cutLength is greater than stockLength')
                bom[key].totalUnitsRequired = '[ERROR: cutLength > stockLength]'
              } else {
                let cutSoFar = 0;
                let i = 0;
                while (cutSoFar < qtyRequired) {
                  if (!(lengthsRequired.length > i)) {
                    lengthsRequired.push({
                      availableLength: stockLength
                    })
                  }
                  if (cutLength <= lengthsRequired[i].availableLength) {
                    lengthsRequired[i].availableLength -= cutLength;
                    cutSoFar++;
                    i = 0;
                  } else {
                    i++;
                  }
                }
              }
            })
            bom[key].lengthsRequired = lengthsRequired.length
            bom[key].totalUnitsRequired = stockLength * lengthsRequired.length
            bom[key].totalCost = bom[key].totalUnitsRequired * bom[key].unitCost
          } else {
            let metresRequired = 0
            parts.forEach(part => {
              let length = part.cutLength / 1000
              metresRequired += length * part.qty * self.qty
            })
            bom[key].totalUnitsRequired = metresRequired
            bom[key].totalCost = metresRequired * bom[key].unitCost
          }
          break
        case 'each':
        default:
          let numberRequired = 0
          parts.forEach(part => {
            numberRequired += part.qty * self.qty
          })
          bom[key].totalUnitsRequired = numberRequired
          bom[key].totalCost = numberRequired * bom[key].unitCost
          break
      }
    })
    self.unsetBom();
    Object.keys(bom).forEach(key => {
      self.addBomEntry({
        material: bom[key].description,
        materialId: bom[key].materialId || null,
        units: bom[key].units,
        unitCost: bom[key].unitCost,
        unitsRequired: bom[key].totalUnitsRequired,
        lengthsRequired: bom[key].lengthsRequired ? bom[key].lengthsRequired : 0
      })
    })
  },
  // recalculateLabour() {
  //   const job = getParent(self, 2)
  //   self.unsetLabour()
  //   job.labour.forEach(op => {
  //     self.addLabourEntry({
  //       description: op.description,
  //       setupMinutes: op.minutesPerSetup * op.numberOfSetups,
  //       operationMinutes: Math.ceil((op.secondsPerOperation * op.numberOfOperations * self.qty) / 60),
  //       extraMinutes: op.extraMinutesPerUnit * self.qty
  //     })
  //   })
  // },
  recalculateAdditionalCosts() {
    const job = getParent(self, 2)
    self.unsetAdditionalCosts()
    job.otherCosts.forEach(cost => {
      self.addAdditionalCostsEntry({
        description: cost.description,
        cost: cost.cost * self.qty
      })
    })
  },
  recalculateAll() {
    self.recalculateBom()
    // self.recalculateLabour()
    self.recalculateAdditionalCosts()
  }
}))



// WEBPACK FOOTER //
// ./src/models/Estimate.js